import Filter from 'components/Filter';
import MapComponent from 'components/MapComponent';
import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getAllCities, getAllPlaces, getPrograms, getTags } from 'services';
import usePlaces from 'store/usePlaces';
import {
  BackDrop,
  BackDropBttom,
  TopBars,
  TripCreatorBtn,
  Wrapper,
} from './Home.styles';
import PopularPlaces from 'components/PopularPlaces';
import useTags from 'store/useTags';
import Search from 'components/Search';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Trip from 'Icons/Trip';
import {
  COMMERCIAL_TAGS,
  POPULAR_SLUG,
  PREFERENCES_SLUGS,
  REGION_TAG,
  REMOVE_FROM_FILTER_TAGS,
  SEARCH_SLUGS,
} from 'utils/constants';
import LangSwitcher from 'components/LangSwitcher';
import useDirections from 'store/useDirections';
import useSearchFilter from 'store/useSearchFilter';
import useRecommended from 'store/useRecommended';
import { getDistance } from 'geolib';
import { getDistanceWithUnit } from 'utils/helpers';
import useAnalytics from 'hooks/useAnalytics';
import useServiceWorker from 'store/useServiceWorker';
import UpdatePopup from 'components/UpdatesPopup';

export default function Home() {
  useAnalytics();

  const {
    setCities,
    setPlaces,
    cityIsSelected,
    allPlaces,
    popPlaces,
    setSeePlaces,
    setAllPlaces,
    setPopPlaces,
    setCitySelected,
    setWithoutCommercialPlaces,
  } = usePlaces();

  const { isDirections } = useDirections();
  const { setTags, tags, setPreferencesTags, resetActiveTags } = useTags();
  const { setSearchFilters } = useSearchFilter();
  const { setPrograms, getDays } = useRecommended();
  const { updated } = useServiceWorker();
  const { data } = useQuery('places', getAllPlaces);
  const { data: fetchedTags } = useQuery('tags', getTags);
  const { data: fetchedCities } = useQuery('cities', getAllCities, {
    refetchOnWindowFocus: false,
  });
  const { data: fetchedPrograms } = useQuery('programs', getPrograms);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const goToCreateTrip = () => {
    navigate('create-program/trip-planer/1');
  };

  //set the places
  useEffect(() => {
    if (data?.data) {
      const filteredPlaces = data.data.filter(
        (place) =>
          place.location?.coordinates?.length !== 0 &&
          !place.location?.coordinates?.includes(null) &&
          place.location?.coordinates[0] < 0 &&
          place.location?.coordinates[0] > -20 &&
          place.location?.coordinates[1] > 15 &&
          place.location?.coordinates[1] < 40,
      );

      const withoutCommercialPlaces = filteredPlaces?.filter(
        (place) =>
          !place.tags.some((tag) => COMMERCIAL_TAGS.includes(tag.slug)),
      );

      const popPlaces = filteredPlaces?.filter((place) => {
        const popTagIndex = place.tags.findIndex(
          (tag) => tag.slug === POPULAR_SLUG,
        );
        return popTagIndex > -1;
      });

      const seePlaces = filteredPlaces?.filter(
        (place) => !place.tags.some((tag) => SEARCH_SLUGS.includes(tag.slug)),
      );

      setWithoutCommercialPlaces(withoutCommercialPlaces);
      setAllPlaces(filteredPlaces);
      setPopPlaces(popPlaces);
      setSeePlaces(seePlaces);
    }
  }, [
    data?.data,
    setAllPlaces,
    setPopPlaces,
    setSeePlaces,
    setWithoutCommercialPlaces,
  ]);

  //set the filters
  useEffect(() => {
    const getCustomTags = (tags, customSlugs) =>
      tags.filter(
        (tag) => tag.slug !== REGION_TAG && customSlugs.includes(tag.slug),
      );

    if (fetchedTags) {
      console.log(fetchedTags.data);
      const filteredTags = fetchedTags.data.filter(
        (tag) =>
          tag.slug !== REGION_TAG &&
          !REMOVE_FROM_FILTER_TAGS.includes(tag.slug) &&
          !SEARCH_SLUGS.includes(tag.slug),
      );
      setTags(filteredTags);
      setPreferencesTags(getCustomTags(fetchedTags.data, PREFERENCES_SLUGS));
      setSearchFilters(getCustomTags(fetchedTags.data, SEARCH_SLUGS));
    }
  }, [fetchedTags, setTags, setPreferencesTags, setSearchFilters]);

  //set the cities
  useEffect(() => {
    if (fetchedCities && data) {
      const regionCities = [];
      data.data.forEach((place) => {
        const newCity = place.contact?.address?.city;
        const index = regionCities.findIndex((city) => city === newCity);

        if (index === -1 && newCity) {
          regionCities.push(newCity);
        }
      });

      const filteredCities = fetchedCities.data
        .filter((city) => regionCities.includes(city.slug))
        .sort((a, b) => a.slug.length - b.slug.length);

      setCities(filteredCities);
      setPlaces(filteredCities);
    }
  }, [fetchedCities, setCities, setCitySelected, setPlaces, data]);

  //set the programs
  useEffect(() => {
    if (fetchedPrograms) {
      const regionPrograms = fetchedPrograms.data.filter((program) =>
        program.places.every((place) =>
          place.tags.some((tag) => tag.slug === REGION_TAG),
        ),
      );

      const adaptedPrograms = regionPrograms
        .filter((program) => program?.showInAMap)
        .map((program) => {
          const image = program.places[0]?.media[0]?.url;
          const firstPlaceCoords = {
            latitude: program.places[0]?.location.coordinates[1],
            longitude: program.places[0]?.location.coordinates[0],
          };

          const lastPlaceCoords = {
            latitude: program.places.slice(-1)[0]?.location?.coordinates[1],
            longitude: program.places.slice(-1)[0]?.location?.coordinates[0],
          };

          let distance = null;
          if (
            firstPlaceCoords.latitude != null &&
            firstPlaceCoords.longitude != null &&
            lastPlaceCoords.latitude != null &&
            lastPlaceCoords.longitude != null
          ) {
            distance = getDistanceWithUnit(
              getDistance(firstPlaceCoords, lastPlaceCoords) * 1.4,
            );
          }

          const regions = [];
          program.places.forEach((place) => {
            const regionSlug = place?.contact?.address?.city;
            if (regionSlug && !regions.includes(regionSlug)) {
              regions.push(regionSlug);
            }
          });

          const days = getDays(program.places);

          return {
            id: program._id,
            title: program.title,
            image,
            distance,
            regions: regions.length,
            days: days.length,
            program: days,
          };
        });
      setPrograms(adaptedPrograms);
    }
  }, [setPrograms, fetchedPrograms, getDays]);

  //reset tags on load
  useEffect(() => {
    resetActiveTags();
  }, [resetActiveTags]);

  const withoutFilter = useMemo(() => {
    const pagesToRestrict = [
      'program-result',
      'program',
      'shared',
      'create-program',
      'poi',
      'suggestion-previw',
    ];
    const page = location.pathname.split('/')[1];
    if (pagesToRestrict.includes(page)) return true;
    return false;
  }, [location.pathname]);

  return (
    <Wrapper>
      <Outlet />
      <MapComponent />
      <BackDrop />
      <BackDropBttom />
      <TopBars isDirections={isDirections}>
        <LangSwitcher />
        <Search places={allPlaces} />
        <PopularPlaces
          filterExist={!withoutFilter}
          isDirections={isDirections}
          selectedCity={cityIsSelected}
          places={popPlaces}
        />

        {tags && !withoutFilter && !isDirections && <Filter tags={tags} />}
      </TopBars>

      <TripCreatorBtn onClick={goToCreateTrip}>
        <span>
          <Trip />
        </span>
        {t('circuitCreator')}
      </TripCreatorBtn>
      {updated && <UpdatePopup />}
    </Wrapper>
  );
}
